import React, { useState, useEffect } from 'react';
import { totpService } from '../../services/totpService';
import './TwoFactorVerification.css';

/**
 * 2단계 인증 확인 컴포넌트
 * 로그인 시 2FA가 활성화된 사용자에게 표시
 */
function TwoFactorVerification({ userId, onVerifySuccess, onCancel }) {
  const [verificationCode, setVerificationCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(30);

  // 타이머 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = totpService.getTimeRemaining();
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 인증 코드 확인
  const handleVerify = async () => {
    if (!verificationCode || (useBackupCode ? verificationCode.length < 8 : verificationCode.length !== 6)) {
      setError(useBackupCode ? '백업 코드를 입력해주세요.' : '6자리 인증 코드를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let isValid = false;

      if (useBackupCode) {
        // 백업 코드 확인
        isValid = await totpService.useBackupCode(userId, verificationCode);
        if (!isValid) {
          setError('유효하지 않거나 이미 사용된 백업 코드입니다.');
        }
      } else {
        // TOTP 코드 확인
        const settings = await totpService.getTwoFactorSettings(userId);
        if (settings) {
          isValid = totpService.verify(verificationCode, settings.secret);
          if (!isValid) {
            setError('인증 코드가 올바르지 않습니다.');
          }
        } else {
          setError('2FA 설정을 찾을 수 없습니다.');
        }
      }

      if (isValid) {
        // 로그인 성공
        onVerifySuccess();
      }
    } catch (err) {
      console.error('2FA 인증 실패:', err);
      setError('인증 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Enter 키 처리
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <div className="two-factor-verification">
      <div className="verification-container">
        <div className="verification-header">
          <h2>2단계 인증</h2>
          <p>계정 보안을 위해 인증 코드를 입력해주세요.</p>
        </div>

        <div className="verification-body">
          {!useBackupCode ? (
            <>
              <div className="code-input-section">
                <label htmlFor="totp-code">인증 앱의 6자리 코드</label>
                <input
                  id="totp-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setVerificationCode(value);
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="123456"
                  className="verification-input"
                  maxLength="6"
                  autoFocus
                  autoComplete="one-time-code"
                />
                <div className="time-indicator">
                  <div 
                    className="time-progress"
                    style={{ width: `${(timeRemaining / 30) * 100}%` }}
                  />
                  <span className="time-text">{timeRemaining}초</span>
                </div>
              </div>

              <button
                className="switch-method-btn"
                onClick={() => {
                  setUseBackupCode(true);
                  setVerificationCode('');
                  setError('');
                }}
              >
                백업 코드 사용
              </button>
            </>
          ) : (
            <>
              <div className="code-input-section">
                <label htmlFor="backup-code">백업 코드</label>
                <input
                  id="backup-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setVerificationCode(value);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="XXXX-XXXX"
                  className="verification-input backup-input"
                  autoFocus
                />
              </div>

              <button
                className="switch-method-btn"
                onClick={() => {
                  setUseBackupCode(false);
                  setVerificationCode('');
                  setError('');
                }}
              >
                인증 앱 사용
              </button>
            </>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="verification-actions">
            <button
              className="cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              취소
            </button>
            <button
              className="verify-btn"
              onClick={handleVerify}
              disabled={loading || !verificationCode}
            >
              {loading ? '확인 중...' : '확인'}
            </button>
          </div>
        </div>

        <div className="verification-footer">
          <p className="help-text">
            인증 앱에 접근할 수 없나요?{' '}
            {!useBackupCode && (
              <button
                className="link-btn"
                onClick={() => {
                  setUseBackupCode(true);
                  setVerificationCode('');
                  setError('');
                }}
              >
                백업 코드를 사용하세요
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default TwoFactorVerification;